import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";

const CreateReimbursementModal = ({ show, onHide, onCreate, employeeId }) => {
  const [formData, setFormData] = useState({
    employee_id: employeeId || "",
    expense_type: "",
    expense_amount: "",
    expense_date: "",
    currency: "INR",
    attachment: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show && employeeId) {
      setFormData((prev) => ({ ...prev, employee_id: employeeId }));
    }
  }, [employeeId, show]);

  const MAX_FILE_SIZE = 50 * 1024; 

  const isValidAttachment = (file) => {
    if (!file) return false;
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    const extOk = /\.(jpe?g|png|pdf)$/i.test(file.name);
    const typeOk = allowedTypes.includes(file.type) || extOk;
    const sizeOk = file.size <= MAX_FILE_SIZE;
    return typeOk && sizeOk;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === "attachment") {
      const file = files && files[0];
      if (file) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
        const extOk = /\.(jpe?g|png|pdf)$/i.test(file.name);
        const typeOk = allowedTypes.includes(file.type) || extOk;
        if (!typeOk) {
          setErrors((prev) => ({ ...prev, attachment: "Only JPG, PNG or PDF files are allowed" }));
          toast.error("Only JPG, PNG or PDF files are allowed for attachment");
          setFormData((prev) => ({ ...prev, attachment: null }));
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          setErrors((prev) => ({ ...prev, attachment: "Attachment must be smaller than 50 KB" }));
          toast.error("Attachment must be smaller than 50 KB");
          setFormData((prev) => ({ ...prev, attachment: null }));
          return;
        }
      }

      setFormData((prev) => ({ ...prev, attachment: file || null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.expense_type.trim()) {
      newErrors.expense_type = "Expense type is required";
    }

    if (!formData.expense_amount || parseFloat(formData.expense_amount) <= 0) {
      newErrors.expense_amount = "Valid amount is required";
    }

    if (!formData.expense_date) {
      newErrors.expense_date = "Expense date is required";
    }

    if (!formData.attachment) {
      newErrors.attachment = "Attachment is required";
    } else if (!isValidAttachment(formData.attachment)) {
      newErrors.attachment = "Only JPG, PNG or PDF files under 50 KB are allowed";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("employee_id", formData.employee_id);
      formDataToSend.append("expense_type", formData.expense_type);
      formDataToSend.append("expense_amount", formData.expense_amount);
      formDataToSend.append("expense_date", formData.expense_date);
      formDataToSend.append("currency", formData.currency.toLowerCase());
      formDataToSend.append("attachment", formData.attachment);

      await onCreate(formDataToSend);
      toast.success("Reimbursement created successfully");
      handleClose();
    } catch (error) {
      console.error("Error creating reimbursement:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create reimbursement";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      employee_id: employeeId || "",
      expense_type: "",
      expense_amount: "",
      expense_date: "",
      currency: "INR",
      attachment: null,
    });
    setErrors({});
    onHide();
  };

  if (!show) return null;

  const expenseTypes = [
    "Food & beverage",
    "Travel & transportation",
    "Accommodation",
    "Office supplies",
    "Entertainment",
    "Miscellaneous",
    "Internet & phone",
    "Medical",
    "Training & development",
    "Client meetings",
    "Postage & courier",
    "Subscriptions",
  ];

  return (
    <>
      <div
        className={`modal custom-modal fade ${show ? "show" : ""}`}
        style={{ display: show ? "block" : "none" }}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Reimbursement</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
                disabled={isSubmitting}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Expense Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-control ${
                      errors.expense_type ? "is-invalid" : ""
                    }`}
                    name="expense_type"
                    value={formData.expense_type}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Select Expense Type</option>
                    {expenseTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.expense_type && (
                    <div className="invalid-feedback">
                      {errors.expense_type}
                    </div>
                  )}
                </div>

                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Amount <span className="text-danger">*</span>
                  </label>
                  <input
                    className={`form-control ${
                      errors.expense_amount ? "is-invalid" : ""
                    }`}
                    type="number"
                    name="expense_amount"
                    value={formData.expense_amount}
                    onChange={handleChange}
                    placeholder="Enter amount"
                    step="0.01"
                    min="0"
                    disabled={isSubmitting}
                  />
                  {errors.expense_amount && (
                    <div className="invalid-feedback">
                      {errors.expense_amount}
                    </div>
                  )}
                </div>

                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Expense Date <span className="text-danger">*</span>
                  </label>
                  <input
                    className={`form-control ${
                      errors.expense_date ? "is-invalid" : ""
                    }`}
                    type="date"
                    name="expense_date"
                    value={formData.expense_date}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  {errors.expense_date && (
                    <div className="invalid-feedback">
                      {errors.expense_date}
                    </div>
                  )}
                </div>

                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Currency <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  >
                    <option value="INR">INR</option>
                  </select>
                </div>

                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Attachment <span className="text-danger">*</span>
                  </label>
                  <input
                    className={`form-control ${
                      errors.attachment ? "is-invalid" : ""
                    }`}
                    type="file"
                    name="attachment"
                    onChange={handleChange}
                    accept=".jpg,.jpeg,.png,.pdf"
                    disabled={isSubmitting}
                  />
                  {errors.attachment && (
                    <div className="invalid-feedback">
                      {errors.attachment}
                    </div>
                  )}
                </div>

                <div className="submit-section">
                  <button
                    type="submit"
                    className="btn btn-primary submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Creating..." : "Create"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary submit-btn"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {show && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default CreateReimbursementModal;
